import { useContext, useMemo, useState } from "react";
import { ParticipantContextProps } from "../../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../../contexts/Participant.context";
import UiTable from "../../../../components/table/Table.component";
import {
	TableColumnTemplate,
	TableColumn,
} from "../../../../models/components/table/table-column.model";
import { AppointmentRow } from "../../../../models/pages/appointments/appointments.model";
import { TablePagination } from "../../../../models/components/table/table-pagination.model";
import { TableMessages } from "../../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../../models/components/field-message.model";
import {
	Appointment,
	AppointmentParticipant,
} from "../../../../models/entities/appointments.model";
import AppointmentDetails from "../../../../components/appointments/AppointmentDetails";
import AppointmentModal from "../../../../components/appointments/AppointmentModal";
import AppointmentModalCancel from "../../../../components/appointments/AppointmentModalCancel";
import {
	AppointmentModalCoach,
	AppointmentModalMode,
} from "../../../../models/components/appointment-modal.model";
import { UpcomingAppointmentRow } from "../../../../models/pages/appointments/upcoming-appointment.model";
import { useAppointmentsCoaches } from "../../../../hooks/useAppointmentsCoaches";
import { useUpcomingAppointments } from "../../../../hooks/useUpcomingAppointments";
import { useAppointmentsTypes } from "../../../../hooks/useAppointmentsTypes";

const ParticipantUpcomingAppointments = () => {
	/* Table */

	// Columns
	const columns: TableColumn[] = [
		{
			field: "type",
			title: "APPOINTMENTS.TABLE.COLUMNS.appointmentType",
			template: TableColumnTemplate.TRANSLATE_PREFIX,
			templateOptions: { translationPrefix: "ENUMS.APPOINTMENTS_TYPE" },
			sortable: true,
		},
		{
			field: "datetime",
			title: "APPOINTMENTS.TABLE.COLUMNS.dateOn",
			sortable: true,
		},
		{
			field: "duration",
			title: "APPOINTMENTS.TABLE.COLUMNS.duration",
			sortable: true,
		},
		{
			field: "calendar",
			title: "APPOINTMENTS.TABLE.COLUMNS.calendar",
		},
		{
			field: "expandCollapse",
			template: TableColumnTemplate.EXPANDER,
			style: {
				minWidth: "120px",
			},
		},
		{
			field: "actionsTemplate",
			template: TableColumnTemplate.BUTTONS,
			style: {
				minWidth: "220px",
			},
		},
	];

	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);
	const { data: appointmentCoaches } = useAppointmentsCoaches();

	// Pagination
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "datetime",
		sortOrder: 1,
		search: undefined,
		filters: undefined,
	});

	const onPagination = async (event: TablePagination): Promise<void> => {
		const newPagination: TablePagination = {
			...pagination,
			page: event.page,
			rows: event.rows,
			sortField: event.sortField,
			sortOrder: event.sortOrder,
			first: event.first,
		};
		setPagination(newPagination);
	};

	/**
	 * ACTION: Reschedule appointment
	 */

	const [rescheduleModalShow, setRescheduleModalShow] = useState<boolean>(false);
	const [rescheduleModalAppointment, setRescheduleModalAppointment] =
		useState<Appointment | null>(null);
	const [rescheduleModalCoach, setRescheduleModalCoach] = useState<AppointmentModalCoach | null>(
		null
	);
	const [rescheduleModalParticipant, setRescheduleModalParticipant] =
		useState<AppointmentParticipant | null>(null);
	const rescheduleModalOpen = (e: Appointment) => {
		setRescheduleModalShow(true);
		setRescheduleModalAppointment(e);
		setRescheduleModalCoach({
			calendarId: e.coachId,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
		setRescheduleModalParticipant(e.participant);
	};

	/**
	 * ACTION: Cancel appointment
	 */

	const [cancelModalShow, setCancelModalShow] = useState<boolean>(false);
	const [cancelModalAppointment, setCancelModalAppointment] = useState<Appointment | null>(null);
	const cancelModalOpen = (e: Appointment) => {
		setCancelModalShow(true);
		setCancelModalAppointment(e);
	};

	/**
	 * DATA
	 */

	const {
		data: appointments,
		isLoading,
		isError,
		refetch,
	} = useUpcomingAppointments(participantContext, pagination);
	const { data: appointmentTypes = [] } = useAppointmentsTypes();

	// Rows
	const rows = useMemo(() => {
		if (!appointments || !appointmentCoaches) return [];

		return appointments
			.filter((x) => new Date(x.datetime).getTime() >= new Date().getTime())
			.map((item) => {
				return new UpcomingAppointmentRow(
					item,
					appointmentTypes,
					appointmentCoaches,
					() => rescheduleModalOpen(item),
					() => cancelModalOpen(item)
				);
			});
	}, [appointments, appointmentTypes, appointmentCoaches, cancelModalOpen, rescheduleModalOpen]);

	// Messages
	const messages = new TableMessages();
	const message: UiFieldMessageProps = useMemo(() => {
		if (isLoading) {
			return {
				severity: FieldMessageSeverity.LOADING,
				label: messages.loading,
			};
		}

		if (isError) {
			return {
				severity: FieldMessageSeverity.DANGER,
				label: messages.error,
			};
		}

		if (!appointments || appointments.length === 0) {
			return {
				severity: FieldMessageSeverity.INFO,
				label: messages.empty,
			};
		}

		return {
			severity: FieldMessageSeverity.INFO,
			label: "",
		};
	}, [isLoading, isError, appointments, messages]);

	return (
		<>
			<UiTable
				useAsCard={false}
				dataKey="id"
				columns={columns}
				value={rows}
				message={message}
				pagination={pagination}
				paginationFn={onPagination}
				paginator={false}
				expandedTemplate={(row: AppointmentRow) => <AppointmentDetails row={row} />}
			/>

			{/* Modal: Cancel appointment */}
			{cancelModalShow && cancelModalAppointment && (
				<AppointmentModalCancel
					isVisible={cancelModalShow}
					appointment={cancelModalAppointment}
					onCancel={() => setCancelModalShow(false)}
					onConfirm={() => {
						setCancelModalShow(false);
						refetch();
					}}
				/>
			)}

			{/* Modal: reschedule appointment */}
			{rescheduleModalShow &&
				rescheduleModalAppointment &&
				rescheduleModalCoach &&
				rescheduleModalParticipant && (
					<AppointmentModal
						mode={AppointmentModalMode.RESCHEDULE}
						isVisible={rescheduleModalShow}
						participant={rescheduleModalParticipant}
						coach={rescheduleModalCoach}
						appointmentCoaches={appointmentCoaches || []}
						appointmentTypes={appointmentTypes}
						appointment={rescheduleModalAppointment}
						onCancel={() => setRescheduleModalShow(false)}
						onConfirm={() => {
							setRescheduleModalShow(false);
							refetch();
						}}
					/>
				)}
		</>
	);
};
export default ParticipantUpcomingAppointments;
